import type { HTMLProps } from 'react';
import type { Control, UseFormReturn } from 'react-hook-form';

import { useCallback } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useUser } from '~/api/user';
import { Alert, Button, ErrorMessage } from '~/components';
import { Field, Input, Switch } from '~/components/Form';
import { EMAIL } from '~/constants/regex';
import { AnchorTargets } from '~/constants/url';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { breakpoints } from '~/styles/media.css';
import { clsx } from '~/styles/utils';
import { isNotBlank } from '~/utils/string';

import type { ContactFormType } from '../types';

import { ContactFormSection } from './ContactFormSection';
import {
  emailsCardActionsStyle,
  emailsCardDeleteButtonStyle,
  emailsCardFieldStyle,
  emailsCardHeaderStyle,
  emailsCardRowLabelStyle,
  emailsCardRowStyle,
  emailsCardsStyle,
  emailsCardStyle,
  emailsRootErrorsStyle,
  emailsTableActionsCellStyle,
  emailsTableAddressCellStyle,
  emailsTableCellStyle,
  emailsTableFooterStyle,
  emailsTableHeaderCellStyle,
  emailsTableRowStyle,
  emailsTableStyle,
  emailsTableSwitchCellStyle,
  emailsTableSwitchWrapperStyle,
  emailsTableWrapperStyle,
  hiddenStyle,
} from './contactFormSections.css';

type ContactEmailsFormType = Pick<ContactFormType, 'emails'>;

const mailTypes = [
  { fieldName: 'enableForQuotations', labelKey: 'quotations' },
  { fieldName: 'enableForInvoicesAndCreditNotes', labelKey: 'invoicesAndCreditNotes' },
  { fieldName: 'enableForPaymentReminders', labelKey: 'paymentReminders' },
] as const;

type MailType = (typeof mailTypes)[number];

export const ContactEmailsFormSection = <T extends ContactEmailsFormType>({
  form,
  peppol,
}: {
  form: T extends ContactEmailsFormType ? UseFormReturn<T> : never;
  peppol: boolean;
}) => {
  const { data: user } = useUser();

  // A table of switches doesn't fit a phone, so each address becomes a card instead.
  const isMobile = useMediaQuery(breakpoints.medium);
  const { t } = useTranslation(['clients', 'common', 'validation']);

  const {
    control,
    formState: { errors, isSubmitted },
    getValues,
    setValue,
    trigger,
    watch,
  } = form;

  const { append, fields, prepend, remove, update } = useFieldArray({
    control,
    name: 'emails',
    rules: {
      validate: {
        // Each mail type should be selected at least once
        eachTypeSelected: (emails) =>
          emails.some((email) => email.enableForQuotations) &&
          emails.some((email) => email.enableForInvoicesAndCreditNotes) &&
          emails.some((email) => email.enableForPaymentReminders),
        // At least one mail type should be selected for each email address
        noEmptySelection: (emails) =>
          !emails.some((email) => !email.enableForQuotations && !email.enableForInvoicesAndCreditNotes && !email.enableForPaymentReminders),
      },
    },
  });

  const addClientEmail = useCallback(() => {
    const userEmails = getValues('emails').filter(({ type }) => type === 'user');
    const quotationsEnabledForUser = userEmails.some((email) => email.enableForQuotations);
    const invoicesAndCreditNotesEnabledForUser = userEmails.some((email) => email.enableForInvoicesAndCreditNotes);
    const paymentRemindersEnabledForUser = userEmails.some((email) => email.enableForPaymentReminders);

    append({
      type: 'client',
      emailAddress: '',
      enableForQuotations: !quotationsEnabledForUser,
      enableForInvoicesAndCreditNotes: !invoicesAndCreditNotesEnabledForUser,
      enableForPaymentReminders: !paymentRemindersEnabledForUser,
    });

    if (isSubmitted) trigger('emails');
  }, [append, getValues, isSubmitted, trigger]);

  const addUserEmail = useCallback(() => {
    if (getValues('emails').some(({ type }) => type === 'user')) return;

    const isEmpty = getValues('emails').length === 1 && getValues('emails.0').emailAddress === '';

    if (isEmpty) remove(0);

    const clientEmails = isEmpty ? [] : getValues('emails').filter(({ type }) => type === 'client');
    const quotationsEnabledForClient = clientEmails.some((email) => email.enableForQuotations);
    const invoicesAndCreditNotesEnabledForClient = clientEmails.some((email) => email.enableForInvoicesAndCreditNotes);
    const paymentRemindersEnabledForClient = clientEmails.some((email) => email.enableForPaymentReminders);

    prepend({
      type: 'user',
      emailAddress: user.email,
      enableForQuotations: !quotationsEnabledForClient,
      enableForInvoicesAndCreditNotes: !invoicesAndCreditNotesEnabledForClient,
      enableForPaymentReminders: !paymentRemindersEnabledForClient,
    });

    if (isSubmitted) trigger('emails');
  }, [getValues, remove, prepend, user.email, isSubmitted, trigger]);

  const removeEmail = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    } else {
      update(index, {
        type: 'client',
        emailAddress: '',
        enableForInvoicesAndCreditNotes: true,
        enableForPaymentReminders: true,
        enableForQuotations: true,
      });
    }

    if (isSubmitted) trigger('emails');
  };

  // A single untouched row can't be removed — there would be nothing left to fill in.
  const isEmpty = watch('emails').length === 1 && watch('emails.0.emailAddress') === '';

  const hasUserEmail = fields.some(({ type }) => type === 'user');

  // A mail type belongs to either the user's or the client's emails, never both.
  const clearMailTypeForOtherContacts = (rowType: 'client' | 'user', fieldName: MailType['fieldName']) => {
    getValues('emails').forEach(({ type }, otherIndex) => {
      if (type !== rowType) setValue(`emails.${otherIndex}.${fieldName}`, false);
    });
  };

  return (
    <ContactFormSection
      description={t('clients:createEdit.subsections.emails.description')}
      id={AnchorTargets.CONTACT_EMAILS}
      title={t('clients:createEdit.subsections.emails.title')}
    >
      {peppol && (
        <Alert announce={false} icon="Peppol" status="info" title="Peppol">
          {t('clients:fields.emailAddresses.peppolDisclaimer')}
        </Alert>
      )}

      {isMobile ? (
        <div className={emailsCardsStyle}>
          {fields.map((field, index) => (
            <div className={emailsCardStyle} key={field.id}>
              <div className={emailsCardHeaderStyle}>
                <FormEmailAddress
                  className={emailsCardFieldStyle}
                  control={control}
                  disabled={field.type === 'user'}
                  index={index}
                  onEnter={addClientEmail}
                />

                <EmailDeleteButton className={emailsCardDeleteButtonStyle} hidden={isEmpty} onClick={() => removeEmail(index)} />
              </div>

              {mailTypes.map((mailType) => (
                <div className={emailsCardRowStyle} key={mailType.fieldName}>
                  <span className={emailsCardRowLabelStyle}>{t(`clients:fields.emailAddresses.${mailType.labelKey}`)}</span>

                  <FormEmailMailType
                    control={control}
                    index={index}
                    mailType={mailType}
                    onCheckedChange={(checked) => {
                      if (checked) clearMailTypeForOtherContacts(field.type, mailType.fieldName);
                      if (isSubmitted) trigger('emails');
                    }}
                  />
                </div>
              ))}
            </div>
          ))}

          <EmailAddButtons
            className={emailsCardActionsStyle}
            onAddClientEmail={addClientEmail}
            onAddUserEmail={addUserEmail}
            showAddUserEmail={!hasUserEmail}
          />
        </div>
      ) : (
        <div className={emailsTableWrapperStyle}>
          <table className={emailsTableStyle}>
            <thead>
              <tr>
                <th className={clsx(emailsTableHeaderCellStyle, emailsTableAddressCellStyle)} scope="col">
                  {t('clients:fields.emailAddresses.label')}
                </th>

                {mailTypes.map(({ labelKey }) => (
                  <th className={clsx(emailsTableHeaderCellStyle, emailsTableSwitchCellStyle)} key={labelKey} scope="col">
                    {t(`clients:fields.emailAddresses.${labelKey}`)}
                  </th>
                ))}

                <th className={clsx(emailsTableHeaderCellStyle, emailsTableActionsCellStyle)} scope="col"></th>
              </tr>
            </thead>

            <tbody>
              {fields.map((field, index) => (
                <tr className={emailsTableRowStyle} key={field.id}>
                  {/* Email address */}
                  <td className={clsx(emailsTableCellStyle, emailsTableAddressCellStyle)}>
                    <FormEmailAddress control={control} disabled={field.type === 'user'} index={index} onEnter={addClientEmail} />
                  </td>

                  {/* Mail type switches */}
                  {mailTypes.map((mailType) => (
                    <td className={clsx(emailsTableCellStyle, emailsTableSwitchCellStyle)} key={mailType.fieldName}>
                      <div className={emailsTableSwitchWrapperStyle}>
                        <FormEmailMailType
                          control={control}
                          index={index}
                          mailType={mailType}
                          onCheckedChange={(checked) => {
                            if (checked) clearMailTypeForOtherContacts(field.type, mailType.fieldName);
                            if (isSubmitted) trigger('emails');
                          }}
                        />
                      </div>
                    </td>
                  ))}

                  {/* Delete */}
                  <td className={clsx(emailsTableCellStyle, emailsTableActionsCellStyle)}>
                    <EmailDeleteButton hidden={isEmpty} onClick={() => removeEmail(index)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <EmailAddButtons
            className={emailsTableFooterStyle}
            onAddClientEmail={addClientEmail}
            onAddUserEmail={addUserEmail}
            showAddUserEmail={!hasUserEmail}
          />
        </div>
      )}

      {/* Root errors */}
      <div className={emailsRootErrorsStyle}>
        {errors.emails?.root?.type === 'eachTypeSelected' && <ErrorMessage>{t('validation:client.emails.eachTypeSelected')}</ErrorMessage>}
        {errors.emails?.root?.type === 'noEmptySelection' && <ErrorMessage>{t('validation:client.emails.noEmptySelection')}</ErrorMessage>}
      </div>
    </ContactFormSection>
  );
};

const FormEmailAddress = <T extends ContactEmailsFormType>({
  control,
  disabled,
  index,
  onEnter,
  ...props
}: HTMLProps<HTMLDivElement> & {
  control: T extends ContactEmailsFormType ? Control<T> : never;
  index: number;
  /** Called when the user confirms the address with the enter key, to add the next one right away. */
  onEnter: () => void;
}) => {
  // The table has a column header, the cards don't.
  const isMobile = useMediaQuery(breakpoints.medium);
  const { t } = useTranslation(['clients', 'validation']);

  return (
    <div {...props}>
      <Controller
        control={control as Control<ContactEmailsFormType>}
        name={`emails.${index}.emailAddress`}
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label srOnly={!isMobile}>{t('clients:fields.emailAddresses.label')}</Field.Label>

            <Input
              disabled={disabled}
              onBlur={onBlur}
              onKeyDown={(e) => e.key === 'Enter' && !e.ctrlKey && !e.shiftKey && !e.metaKey && onEnter()}
              onValueChange={onChange}
              placeholder={t('clients:fields.emailAddresses.placeholder')}
              ref={ref}
              type="email"
              value={value}
            />

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{
          pattern: { message: t('validation:email.invalid'), value: EMAIL },
          required: { value: true, message: t('validation:required') },
          validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
        }}
      />
    </div>
  );
};

const FormEmailMailType = <T extends ContactEmailsFormType>({
  control,
  index,
  mailType,
  onCheckedChange,
}: {
  control: T extends ContactEmailsFormType ? Control<T> : never;
  index: number;
  mailType: MailType;
  onCheckedChange: (checked: boolean) => void;
}) => {
  const { t } = useTranslation(['clients']);

  return (
    <Controller
      control={control as Control<ContactEmailsFormType>}
      name={`emails.${index}.${mailType.fieldName}`}
      render={({ field: { onBlur, onChange, value } }) => (
        <Switch.Root
          aria-label={t(`clients:fields.emailAddresses.${mailType.labelKey}`)}
          checked={value}
          onBlur={onBlur}
          onCheckedChange={(checked) => {
            onChange(checked);
            onCheckedChange(checked);
          }}
        />
      )}
    />
  );
};

const EmailDeleteButton = ({ className, hidden, onClick }: { className?: string; hidden: boolean; onClick: () => void }) => {
  const { t } = useTranslation(['common']);

  return (
    <Button
      aria-label={t('common:actions.delete')}
      className={clsx(className, hidden && hiddenStyle)}
      emphasis="tertiary"
      icon="Trash"
      onClick={onClick}
    />
  );
};

const EmailAddButtons = ({
  onAddClientEmail,
  onAddUserEmail,
  showAddUserEmail,
  ...props
}: HTMLProps<HTMLDivElement> & {
  onAddClientEmail: () => void;
  onAddUserEmail: () => void;
  showAddUserEmail: boolean;
}) => {
  const { t } = useTranslation(['clients']);

  return (
    <div {...props}>
      <Button icon="Add" onClick={onAddClientEmail}>
        {t('clients:fields.emailAddresses.addReceiver')}
      </Button>

      {showAddUserEmail && (
        <Button emphasis="secondary" icon="Add" onClick={onAddUserEmail}>
          {t('clients:fields.emailAddresses.addSelf')}
        </Button>
      )}
    </div>
  );
};
